<script lang="ts" setup>
import { defineModel, computed } from 'vue';
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour';
import { usePaginationMetadata } from '~/app/composables/usePaginationMetadata';

interface Props {
  totalPosts: number,
  itemsPerPage?: number,
  siblingCount?: number,
  listingKey?: string,
}

const props = withDefaults(defineProps<Props>(), {
  itemsPerPage: 12,
  siblingCount: 1,
  listingKey: undefined,
});

const page = defineModel<number>();
const { generatePaginationLinksForUI } = usePaginationMetadata();

// Generate pagination links
const paginationLinks = computed(() => {
  if (!props.listingKey) { return []; }
  return generatePaginationLinksForUI(props.totalPosts, props.itemsPerPage, props.listingKey);
});

// Handle page click
function handlePageClick(pageNumber: number) {
  page.value = pageNumber;
}
</script>

<template>
  <UiPagination
    :sibling-count="props.siblingCount"
    :total="props.totalPosts"
    class="c-pagination amp-auto-track"
    :data-analytics-click-behaviour="AnalyticsClickBehaviour.Pagination"
    :items-per-page="props.itemsPerPage"
    show-edges
    :page="page"
  >
    <UiPaginationList v-slot="{ items }" class="c-pagination__container">
      <template v-for="(item, index) in items">
        <UiPaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
          <NuxtLink
            v-if="paginationLinks.find((link) => link.page === item.value)?.href"
            :to="paginationLinks.find((link) => link.page === item.value)?.href"
            class="c-pagination__item c-pagination__item--number"
            :class="{'c-pagination__item--active': item.value === page}"
            @click="handlePageClick(item.value)"
          >
            {{ item.value }}
          </NuxtLink>
        </UiPaginationListItem>
        <UiPaginationEllipsis v-else :key="item.type" class="c-pagination__item  c-pagination__item--icon c-pagination__item--ellipsis" :index="index" />
      </template>
    </UiPaginationList>
  </UiPagination>
</template>

<style scoped>

.c-pagination__container {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: theme('spacing.72');
}

.c-pagination__item {
  cursor: pointer;
  height: 32px;
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--pagination-text-color);

  @screen tablet-wide {
    height: 40px;
    width: 40px;
  }

  &--number {
    border: 1px solid var(--pagination-border-color);
  }

  &--icon {
    width: 11px;

    @screen tablet-wide {
      width: 13px;
    }
  }

  &--ellipsis {
    pointer-events: none;
  }

  &[data-selected="true"] {
    border-color: var(--pagination-border-color-active);
  }

  &:hover {
    color: var(--pagination-text-color-hover);
    border-color: var(--pagination-border-color-hover);
  }
}

</style>
